import { TRIGGERS, VARIABLES, type Step, type TriggerType } from '@ly360/core';
import { Dialog, Empty, ErrorState, Loading, PageHead, Select, useFormat } from '@ly360/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  Bell,
  CheckCheck,
  Clock,
  ListChecks,
  MessageSquare,
  OctagonX,
  Plus,
  Tag,
  Trash2,
  TriangleAlert,
  Zap,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Field } from '../crm/forms';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useSegments } from '../retail/data';
import { DialogButtons } from '../retail/ui';

export interface Automation {
  id: string;
  recipe: string | null;
  name: string;
  nameAr: string | null;
  trigger: { type: TriggerType; days?: number };
  conditions: { all: unknown[] };
  steps: Step[];
  mode: 'auto' | 'manual';
  enabled: boolean;
  purpose: 'service' | 'marketing';
  offerCode: string | null;
  active: number;
  pending: number;
  sent30: number;
  skipped30: number;
  orders30: number;
  revenue30Minor: string;
}
interface Run {
  id: string;
  status: string;
  personId: string | null;
  personName: string | null;
  sentAt: string | null;
  runAt: string;
  createdAt: string;
  conversationId: string | null;
  log: { at?: string; what: string; channel?: string; reason?: string; until?: string; why?: string }[];
}

const STEP_ICON = {
  wait: Clock,
  send: MessageSquare,
  stop_if: OctagonX,
  tag: Tag,
  task: ListChecks,
} as const;

export function useAutomationName() {
  const { i18n, t } = useTranslation();
  return (a: { recipe: string | null; name: string; nameAr: string | null }) =>
    a.recipe ? t(`automations.recipe.${a.recipe}.name`) : (i18n.language === 'ar' && a.nameAr) || a.name;
}

/** One step in plain words ("Wait 1 hour", "Send on WhatsApp, else email: cart_reminder"). */
export function useStepText() {
  const { t } = useTranslation();
  return (s: Step) => {
    switch (s.type) {
      case 'wait':
        return s.minutes % 1440 === 0
          ? t('automations.step.waitDays', { count: s.minutes / 1440 })
          : s.minutes % 60 === 0
            ? t('automations.step.waitHours', { count: s.minutes / 60 })
            : t('automations.step.waitMinutes', { count: s.minutes });
      case 'send':
        return t('automations.step.send', {
          channels: s.channels.map((c) => t(`channels.${c}`)).join(` ${t('automations.step.else')} `),
          template: s.template ?? t('automations.step.freeText'),
        });
      case 'stop_if':
        return t(`automations.step.stop.${s.condition}`);
      case 'tag':
        return t('automations.step.tag', { tags: s.tags.join(', ') });
      case 'task':
        return t('automations.step.task', { title: s.title });
    }
  };
}

export function AutomationsPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const { can, session } = useAuth();
  const qc = useQueryClient();
  const name = useAutomationName();
  const stepText = useStepText();
  const [open, setOpen] = useState<Automation | null>(null);
  const [building, setBuilding] = useState(false);
  const q = useQuery({
    queryKey: ['automations'],
    queryFn: () =>
      api<{ items: Automation[]; templates: { name: string; approved: boolean }[] }>('/v1/automations'),
  });
  const patch = useMutation({
    mutationFn: ({ id, ...body }: { id: string; enabled?: boolean; mode?: 'auto' | 'manual' }) =>
      api(`/v1/automations/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['automations'] }),
  });
  const manage = can('automations:manage');
  const missing = new Set((q.data?.templates ?? []).filter((x) => !x.approved).map((x) => x.name));
  const pending = (q.data?.items ?? []).reduce((s, a) => s + a.pending, 0);
  const currency = session!.tenant.currency;

  return (
    <>
      <PageHead
        eyebrow={t('nav.marketing')}
        title={t('automations.title')}
        subtitle={t('automations.subtitle')}
        actions={
          <>
            {can('automations:approve') && (
              <Link to="/automations/approvals" className="btn" data-testid="approvals-link">
                <CheckCheck size={16} />
                {t('automations.approvals')}
                {pending > 0 && <span className="tab-count">{fmt.number(pending)}</span>}
              </Link>
            )}
            {manage && (
              <button className="btn btn-grad" onClick={() => setBuilding(true)} data-testid="new-automation">
                <Plus size={16} />
                {t('automations.new')}
              </button>
            )}
          </>
        }
      />
      <p className="notice" style={{ marginBottom: '1rem' }}>
        <Zap size={16} aria-hidden="true" />
        <span className="grow">{t('automations.howItWorks')}</span>
      </p>
      {q.isLoading && <Loading />}
      {q.isError && <ErrorState error={q.error} />}
      <div className="automation-grid" data-testid="automations">
        {q.data?.items.map((a) => {
          const needs = a.steps.flatMap((s) =>
            s.type === 'send' && s.template && missing.has(s.template) && s.channels.includes('whatsapp')
              ? [s.template]
              : [],
          );
          return (
            <section
              key={a.id}
              className={`card card-pad automation-card${a.enabled ? ' is-on' : ''}`}
              data-testid={`automation-${a.recipe ?? a.id}`}
            >
              <div className="automation-head">
                <div className="grow">
                  <h2 dir="auto">{name(a)}</h2>
                  <span className={`badge ${a.purpose === 'service' ? 'badge-neutral' : 'badge'}`}>
                    {t(`automations.purpose.${a.purpose}`)}
                  </span>
                </div>
                <label className="toggle" title={a.enabled ? t('automations.on') : t('automations.off')}>
                  <input
                    type="checkbox"
                    role="switch"
                    checked={a.enabled}
                    disabled={!manage || patch.isPending}
                    onChange={(e) => patch.mutate({ id: a.id, enabled: e.target.checked })}
                    aria-label={t('automations.switch', { name: name(a) })}
                    data-testid="automation-toggle"
                  />
                  <span aria-hidden="true" />
                </label>
              </div>
              {a.recipe && (
                <p className="faint" style={{ margin: 0 }}>
                  {t(`automations.recipe.${a.recipe}.description`)}
                </p>
              )}
              <ol className="step-list">
                <li className="step-trigger">
                  <Zap size={13} />
                  {t(`automations.trigger.${a.trigger.type.replace('.', '_')}`, {
                    days: a.trigger.days ?? 120,
                  })}
                </li>
                {a.steps.map((s, i) => {
                  const Icon = STEP_ICON[s.type];
                  return (
                    <li key={i}>
                      <ArrowDown size={11} className="faint step-arrow" aria-hidden="true" />
                      <Icon size={13} />
                      <span dir="auto">{stepText(s)}</span>
                    </li>
                  );
                })}
              </ol>
              {needs.length > 0 && (
                <p className="notice warn automation-warn">
                  <TriangleAlert size={14} aria-hidden="true" />
                  <span className="grow">{t('automations.needsTemplate', { names: needs.join(', ') })}</span>
                  <Link to={`/admin/whatsapp?create=${encodeURIComponent(needs[0]!)}`} className="btn btn-sm">
                    {t('automations.createTemplate')}
                  </Link>
                </p>
              )}
              <div className="automation-foot">
                <div className="segmented" role="group" aria-label={t('automations.mode')}>
                  {(['auto', 'manual'] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      aria-pressed={a.mode === m}
                      disabled={!manage}
                      onClick={() => a.mode !== m && patch.mutate({ id: a.id, mode: m })}
                      data-testid={`mode-${m}`}
                      title={t(`automations.modeHint.${m}`)}
                    >
                      {t(`automations.modes.${m}`)}
                    </button>
                  ))}
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => setOpen(a)}>
                  {t('automations.activity')}
                </button>
              </div>
              <dl className="automation-stats">
                <div>
                  <dt>{t('automations.stats.sent')}</dt>
                  <dd>{fmt.number(a.sent30)}</dd>
                </div>
                <div>
                  <dt>{t('automations.stats.sales')}</dt>
                  <dd>{fmt.money(Number(a.revenue30Minor), currency)}</dd>
                </div>
                <div>
                  <dt>{t('automations.stats.waiting')}</dt>
                  <dd>
                    {a.pending ? (
                      <Link to="/automations/approvals">
                        {t('automations.stats.toApprove', { count: a.pending })}
                      </Link>
                    ) : (
                      fmt.number(a.active)
                    )}
                  </dd>
                </div>
              </dl>
            </section>
          );
        })}
      </div>
      {open && <RunsDialog automation={open} onClose={() => setOpen(null)} />}
      {building && <BuilderDialog onClose={() => setBuilding(false)} />}
    </>
  );
}

function RunsDialog({ automation, onClose }: { automation: Automation; onClose: () => void }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const name = useAutomationName();
  const q = useQuery({
    queryKey: ['automations', automation.id],
    queryFn: () => api<{ runs: Run[] }>(`/v1/automations/${automation.id}`),
  });
  return (
    <Dialog open wide title={name(automation)} onClose={onClose} testId="automation-runs">
      {q.isLoading && <Loading />}
      {q.data && !q.data.runs.length && <Empty text={t('automations.noActivity')} />}
      {!!q.data?.runs.length && (
        <div className="table-wrap">
          <table className="table table-responsive">
            <thead>
              <tr>
                <th>{t('retail.col.customer')}</th>
                <th>{t('retail.col.status')}</th>
                <th>{t('automations.lastStep')}</th>
              </tr>
            </thead>
            <tbody>
              {q.data.runs.map((r) => {
                const last = r.log.at(-1);
                return (
                  <tr key={r.id}>
                    <td data-label={t('retail.col.customer')}>
                      {r.personId ? (
                        <Link to={`/people/${r.personId}`} dir="auto">
                          {r.personName}
                        </Link>
                      ) : (
                        '—'
                      )}
                      <span className="faint" style={{ display: 'block', fontSize: 'var(--t-xs)' }}>
                        {fmt.dateTime(r.createdAt)}
                      </span>
                    </td>
                    <td data-label={t('retail.col.status')}>
                      <span className="badge badge-neutral">{t(`automations.runStatus.${r.status}`)}</span>
                    </td>
                    <td data-label={t('automations.lastStep')}>
                      {last
                        ? t(`automations.log.${last.what.replace(/ /g, '_')}`, {
                            defaultValue: last.what,
                            ...last,
                          })
                        : '—'}
                      {last?.reason && (
                        <span className="faint">
                          {' '}
                          ({t(`automations.reason.${last.reason}`, { defaultValue: last.reason })})
                        </span>
                      )}
                      {r.status === 'waiting' && (
                        <span className="faint" style={{ display: 'block', fontSize: 'var(--t-xs)' }}>
                          {t('automations.next', { when: fmt.dateTime(r.runAt) })}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Dialog>
  );
}

const blankStep = (type: Step['type']): Step => {
  switch (type) {
    case 'wait':
      return { type, minutes: 60 };
    case 'send':
      return { type, channels: ['whatsapp', 'email'], template: null, text: { en: '', ar: '' } };
    case 'stop_if':
      return { type, condition: 'ordered_since' };
    case 'tag':
      return { type, tags: ['follow-up'] };
    case 'task':
      return { type, title: '', dueMinutes: 60 };
  }
};

/** Build an automation: when → (only for people who match an audience) → steps; auto or manual. */
function BuilderDialog({ onClose }: { onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const stepText = useStepText();
  const segments = useSegments();
  const [name, setName] = useState('');
  const [trigger, setTrigger] = useState<TriggerType>('order.delivered');
  const [days, setDays] = useState(120);
  const [segmentId, setSegmentId] = useState('');
  const [purpose, setPurpose] = useState<'service' | 'marketing'>('marketing');
  const [mode, setMode] = useState<'auto' | 'manual'>('manual');
  const [offerCode, setOfferCode] = useState('');
  const [steps, setSteps] = useState<Step[]>([blankStep('send')]);
  const [adding, setAdding] = useState('');
  const save = useMutation({
    mutationFn: () =>
      api('/v1/automations', {
        method: 'POST',
        body: JSON.stringify({
          name,
          trigger: { type: trigger, ...(trigger === 'customer.lapsing' && { days }) },
          conditions: segments.data?.items.find((s) => s.id === segmentId)?.rules ?? { all: [] },
          steps,
          purpose,
          mode,
          offerCode: offerCode.trim() || null,
          enabled: true,
        }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['automations'] });
      onClose();
    },
  });
  const update = (i: number, s: Step) => setSteps((xs) => xs.map((x, j) => (j === i ? s : x)));
  const ar = i18n.language === 'ar';
  return (
    <Dialog
      open
      wide
      title={t('automations.new')}
      onClose={onClose}
      onSubmit={() => save.mutate()}
      testId="automation-builder"
      footer={
        <DialogButtons
          onClose={onClose}
          busy={save.isPending}
          disabled={!steps.length}
          label={t('automations.create')}
        />
      }
    >
      <div className="form-grid">
        <Field id="ab-name" label={t('automations.field.name')}>
          <input
            id="ab-name"
            className="input"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field id="ab-trigger" label={t('automations.field.trigger')}>
          <Select
            id="ab-trigger"
            value={trigger}
            onChange={(v) => setTrigger(v as TriggerType)}
            options={TRIGGERS.map((x) => ({
              value: x,
              label: t(`automations.trigger.${x.replace('.', '_')}`, { days }),
            }))}
          />
        </Field>
        {trigger === 'customer.lapsing' && (
          <Field id="ab-days" label={t('automations.field.days')}>
            <input
              id="ab-days"
              className="input"
              type="number"
              min={14}
              max={720}
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            />
          </Field>
        )}
        <Field id="ab-segment" label={t('automations.field.onlyFor')}>
          <Select
            id="ab-segment"
            value={segmentId}
            onChange={setSegmentId}
            options={[
              { value: '', label: t('automations.field.everyone') },
              ...(segments.data?.items ?? []).map((s) => ({
                value: s.id,
                label: (ar && s.nameAr) || s.name,
              })),
            ]}
          />
        </Field>
        <Field
          id="ab-purpose"
          label={t('automations.field.purpose')}
          hint={t(`automations.purposeHint.${purpose}`)}
        >
          <Select
            id="ab-purpose"
            value={purpose}
            onChange={(v) => setPurpose(v as 'service' | 'marketing')}
            options={(['marketing', 'service'] as const).map((p) => ({
              value: p,
              label: t(`automations.purpose.${p}`),
            }))}
          />
        </Field>
        <Field id="ab-mode" label={t('automations.mode')} hint={t(`automations.modeHint.${mode}`)}>
          <Select
            id="ab-mode"
            value={mode}
            onChange={(v) => setMode(v as 'auto' | 'manual')}
            options={(['auto', 'manual'] as const).map((m) => ({
              value: m,
              label: t(`automations.modes.${m}`),
            }))}
          />
        </Field>
        <Field id="ab-code" label={t('retail.campaign.offerCode')}>
          <input
            id="ab-code"
            className="input mono"
            dir="ltr"
            value={offerCode}
            onChange={(e) => setOfferCode(e.target.value.toUpperCase())}
          />
        </Field>
      </div>
      <h3 className="section-title" style={{ marginTop: '1rem' }}>
        {t('automations.steps')}
      </h3>
      <p className="faint" style={{ marginTop: 0 }}>
        {t('automations.variables', { names: VARIABLES[trigger].map((v) => `{{${v}}}`).join(' ') })}
      </p>
      <ol className="rule-editor" data-testid="step-editor">
        {steps.map((s, i) => (
          <li key={i} className="rule-edit">
            <div className="rule-edit-head">
              <strong>{stepText(s)}</strong>
              <button
                type="button"
                className="icon-btn"
                aria-label={t('common.remove')}
                onClick={() => setSteps((xs) => xs.filter((_, j) => j !== i))}
              >
                <Trash2 size={14} />
              </button>
            </div>
            <StepFields step={s} onChange={(x) => update(i, x)} />
          </li>
        ))}
      </ol>
      <div className="rule-add">
        <Select
          value={adding}
          onChange={(v) => {
            if (!v) return;
            setSteps((xs) => [...xs, blankStep(v as Step['type'])]);
            setAdding('');
          }}
          placeholder={t('automations.addStep')}
          aria-label={t('automations.addStep')}
          data-testid="add-step"
          options={(['send', 'wait', 'stop_if', 'tag', 'task'] as const).map((x) => ({
            value: x,
            label: t(`automations.stepType.${x}`),
          }))}
        />
      </div>
      {save.isError && <ErrorState error={save.error} />}
    </Dialog>
  );
}

function StepFields({ step: s, onChange }: { step: Step; onChange: (s: Step) => void }) {
  const { t } = useTranslation();
  const options = useQuery({
    queryKey: ['campaigns', 'options'],
    queryFn: () => api<{ templates: { name: string }[] }>('/v1/campaigns/options'),
  });
  switch (s.type) {
    case 'wait':
      return (
        <label className="rule-num">
          <span>{t('automations.field.minutes')}</span>
          <input
            className="input"
            type="number"
            min={1}
            value={s.minutes}
            onChange={(e) => onChange({ ...s, minutes: Math.max(1, Number(e.target.value)) })}
          />
        </label>
      );
    case 'send':
      return (
        <div className="rule-stack">
          <fieldset className="chip-set">
            <legend className="sr-only">{t('automations.field.channels')}</legend>
            {(['whatsapp', 'email', 'sms'] as const).map((c) => (
              <label key={c} className="chip chip-toggle">
                <input
                  type="checkbox"
                  checked={s.channels.includes(c)}
                  onChange={(e) =>
                    onChange({
                      ...s,
                      channels: e.target.checked ? [...s.channels, c] : s.channels.filter((x) => x !== c),
                    })
                  }
                />
                {t(`channels.${c}`)}
              </label>
            ))}
          </fieldset>
          <Select
            value={s.template ?? ''}
            onChange={(v) => onChange({ ...s, template: v || null, params: ['name', 'order', 'total'] })}
            aria-label={t('retail.campaign.template')}
            options={[
              { value: '', label: t('automations.field.noTemplate') },
              ...(options.data?.templates ?? []).map((x) => ({ value: x.name, label: x.name })),
            ]}
          />
          <textarea
            className="textarea"
            rows={2}
            dir="auto"
            placeholder={t('retail.campaign.messageEn')}
            value={s.text?.en ?? ''}
            onChange={(e) => onChange({ ...s, text: { ...s.text, en: e.target.value } })}
          />
          <textarea
            className="textarea"
            rows={2}
            dir="rtl"
            lang="ar"
            placeholder={t('retail.campaign.messageAr')}
            value={s.text?.ar ?? ''}
            onChange={(e) => onChange({ ...s, text: { ...s.text, ar: e.target.value } })}
          />
        </div>
      );
    case 'stop_if':
      return (
        <Select
          value={s.condition}
          onChange={(v) => onChange({ ...s, condition: v as typeof s.condition })}
          aria-label={t('automations.stepType.stop_if')}
          options={(['ordered_since', 'replied_since', 'checkout_recovered', 'order_cancelled'] as const).map(
            (c) => ({
              value: c,
              label: t(`automations.step.stop.${c}`),
            }),
          )}
        />
      );
    case 'tag':
      return (
        <input
          className="input"
          aria-label={t('automations.stepType.tag')}
          defaultValue={s.tags.join(', ')}
          onBlur={(e) =>
            onChange({
              ...s,
              tags: e.target.value
                .split(',')
                .map((x) => x.trim())
                .filter(Boolean)
                .slice(0, 5),
            })
          }
        />
      );
    case 'task':
      return (
        <div className="rule-nums">
          <input
            className="input"
            aria-label={t('automations.field.taskTitle')}
            placeholder={t('automations.field.taskTitle')}
            value={s.title}
            onChange={(e) => onChange({ ...s, title: e.target.value })}
          />
          <label className="rule-num">
            <span>{t('automations.field.dueMinutes')}</span>
            <input
              className="input"
              type="number"
              min={0}
              value={s.dueMinutes}
              onChange={(e) => onChange({ ...s, dueMinutes: Number(e.target.value) })}
            />
          </label>
        </div>
      );
  }
}

interface Pending {
  id: string;
  pending: { channel: string; text: string; template: string | null; language: string } | null;
  since: string;
  personId: string | null;
  personName: string | null;
  automationId: string;
  automationName: string;
  automationNameAr: string | null;
  purpose: string;
}

/** Messages from automations in manual mode, waiting for a person: send as is, edit and send, or skip. */
export function ApprovalsPage() {
  const { t, i18n } = useTranslation();
  const fmt = useFormat();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Record<string, string>>({});
  // New messages are held while the page is open (orders keep coming in), so keep the list current.
  const q = useQuery({
    queryKey: ['approvals'],
    queryFn: () => api<{ items: Pending[] }>('/v1/automations/approvals'),
    refetchInterval: 10_000,
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['approvals'] });
    void qc.invalidateQueries({ queryKey: ['automations'] });
  };
  const approve = useMutation({
    mutationFn: ({ id, text }: { id: string; text?: string }) =>
      api(`/v1/automations/runs/${id}/approve`, {
        method: 'POST',
        body: JSON.stringify(text ? { text } : {}),
      }),
    onSuccess: refresh,
  });
  const skip = useMutation({
    mutationFn: (id: string) => api(`/v1/automations/runs/${id}/skip`, { method: 'POST', body: '{}' }),
    onSuccess: refresh,
  });
  const all = useMutation({
    mutationFn: (id: string) => api(`/v1/automations/${id}/approve-all`, { method: 'POST', body: '{}' }),
    onSuccess: refresh,
  });
  const groups = new Map<string, Pending[]>();
  for (const p of q.data?.items ?? []) groups.set(p.automationId, [...(groups.get(p.automationId) ?? []), p]);
  const ar = i18n.language === 'ar';
  return (
    <>
      <PageHead
        eyebrow={t('automations.title')}
        title={t('automations.approvals')}
        subtitle={t('automations.approvalsHint')}
      />
      {q.isLoading && <Loading />}
      {q.data && !q.data.items.length && <Empty text={t('automations.nothingToApprove')} />}
      {[...groups.entries()].map(([automationId, items]) => (
        <section
          key={automationId}
          className="card"
          style={{ marginBottom: '1rem' }}
          data-testid="approval-group"
        >
          <div className="card-head">
            <h2 dir="auto">{(ar && items[0]!.automationNameAr) || items[0]!.automationName}</h2>
            <button
              className="btn btn-sm btn-grad"
              onClick={() => all.mutate(automationId)}
              disabled={all.isPending}
              data-testid="approve-all"
            >
              <CheckCheck size={14} />
              {t('automations.approveAll', { count: items.length })}
            </button>
          </div>
          <ul className="list-plain approval-list">
            {items.map((p) => (
              <li key={p.id} data-testid="approval">
                <div className="approval-who">
                  {p.personId ? (
                    <Link to={`/people/${p.personId}`} dir="auto">
                      <strong>{p.personName}</strong>
                    </Link>
                  ) : (
                    <strong>—</strong>
                  )}
                  <span className="faint">
                    {p.pending && t(`channels.${p.pending.channel}`)} · {fmt.relative(p.since)}
                  </span>
                </div>
                {editing[p.id] !== undefined ? (
                  <textarea
                    className="textarea"
                    rows={3}
                    dir="auto"
                    value={editing[p.id]}
                    onChange={(e) => setEditing({ ...editing, [p.id]: e.target.value })}
                    aria-label={t('automations.editMessage')}
                  />
                ) : (
                  <p className="bubble out approval-text" dir="auto">
                    {p.pending?.text}
                  </p>
                )}
                {editing[p.id] !== undefined && p.pending?.template && (
                  <p className="faint" style={{ fontSize: 'var(--t-xs)', margin: 0 }}>
                    {t('automations.editNote')}
                  </p>
                )}
                <div className="quick-actions">
                  <button
                    className="btn btn-sm btn-grad"
                    onClick={() => approve.mutate({ id: p.id, text: editing[p.id] })}
                    disabled={approve.isPending}
                    data-testid="approve"
                  >
                    <Bell size={14} />
                    {editing[p.id] !== undefined ? t('automations.sendEdited') : t('automations.send')}
                  </button>
                  {editing[p.id] === undefined && (
                    <button
                      className="btn btn-sm"
                      onClick={() => setEditing({ ...editing, [p.id]: p.pending?.text ?? '' })}
                    >
                      {t('automations.edit')}
                    </button>
                  )}
                  <button
                    className="btn btn-sm btn-ghost"
                    onClick={() => skip.mutate(p.id)}
                    data-testid="skip"
                  >
                    {t('automations.skip')}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}
      {(approve.isError || skip.isError || all.isError) && (
        <ErrorState error={approve.error ?? skip.error ?? all.error} />
      )}
    </>
  );
}
